import {catchError, from, map, Observable, tap} from 'rxjs';
import {AxiosError, AxiosResponse} from 'axios';

import {statisticsStore} from '@store/statistics/statistics.store';
import {
  ClientTurnover,
  EvolutionOfOperations,
  ExecutionCountByServiceKind,
  InvoicingProgression,
  MarginByLine,
  StatisticsFilters,
  StatisticsMode,
  TotalTurnoverAndCharges,
  TurnoverAndPurchase,
  TurnoverChargesByServiceKind,
  ValoWeightByWasteFamily,
} from '@store/statistics/statistics.model';
import {
  getEvolutionOfOperationsDataSource,
  getExecutionCountAll,
  getExecutionCountOther,
  getExecutionCountPickup,
  getExecutionCountProcessingValo,
  getExecutionCountRenting,
  getInvoicingProgression,
  getMarginByLineDataSource,
  getMostProfitableClients,
  getTotalTurnoverNewDataSource,
  getTurnoverAndPurchaseDataSource,
  getTurnoverChargesServiceKind,
  getValoWeightByWasteFamily,
} from '@store/statistics/statistics.requests';
import SnackError from '@utils/error.utils';
import APIAxios, {APIRoutes} from '@api/axios.api';
import {TableFilter} from '@store/common/tableFilter.model';

export class StatisticsService {
  store = statisticsStore;

  setFilters = (filters: Partial<StatisticsFilters>) =>
    this.store.update((state) => ({
      ...state,
      filters: {...state.filters, ...filters},
    }));

  resetFilters = () => this.store.update((state) => ({
    ...state,
    filters: {
      mode: StatisticsMode.MONTH,
      date: new Date(),
      tableFilters: StatisticsFilters.initTableFilters(),
    }
  }));

  //INVOICING

  getMostProfitableClients = (filters: StatisticsFilters): Observable<ClientTurnover[]> => {
    return from(APIAxios({
      ...APIRoutes.GETMostProfitableClients(),
      params: {
        date: filters.date.toISOString(),
        granularity: filters.mode,
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<ClientTurnover[]>) => {
          return response.data;
        }),
        tap((clientsTurnover) => {
          this.store.update(
            (state) => ({
              ...state,
              finance: {...state.finance, clientsTurnover}
            }),
            getMostProfitableClients.setSuccess()
          );
        }),
        getMostProfitableClients.trackRequestStatus(),
      );
  };

  getTurnoverAndPurchase = (filters: StatisticsFilters): Observable<TurnoverAndPurchase[]> => {
    const requestFilters = TableFilter.toRequestFilters(filters.tableFilters);
    return from(APIAxios({
      ...APIRoutes.GETTurnoverAndPurchase(),
      params: {
        ...requestFilters,
        date: filters.date.toISOString(),
        granularity: filters.mode,
      }
    }))
      .pipe(
        catchError((err: AxiosError) => {
          throw new SnackError((err.response as any)?.data?.message, 'error');
        }),
        map((response: AxiosResponse<TurnoverAndPurchase[]>) => {
          return response.data;
        }),
        tap((turnoverAndPurchaseEvolution) => {
          this.store.update(
            (state) => ({
              ...state,
              finance: {...state.finance, turnoverAndPurchaseEvolution},
            }),
            getTurnoverAndPurchaseDataSource.setSuccess()
          );
        }),
        getTurnoverAndPurchaseDataSource.trackRequestStatus()
      );
  };

  getTotalTurnoverNew = (filters: StatisticsFilters): Observable<TotalTurnoverAndCharges> => {
    const requestFilters = TableFilter.toRequestFilters(filters.tableFilters);
    return from(
      APIAxios({
        ...APIRoutes.GETTotalTurnoverNew(),
        params: {
          ...requestFilters,
          date: filters.date.toISOString(),
          granularity: filters.mode,
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<TotalTurnoverAndCharges>) => {
        return response.data;
      }),
      tap((totalTurnover) => {
        this.store.update(
          (state) => ({
            ...state,
            finance: {...state.finance, totalTurnover},
          }),
          getTotalTurnoverNewDataSource.setSuccess()
        );
      }),
      getTotalTurnoverNewDataSource.trackRequestStatus()
    );
  };

  getInvoicingProgression = (filters: StatisticsFilters): Observable<InvoicingProgression> => {
    const requestFilters = TableFilter.toRequestFilters(filters.tableFilters);
    return from(
      APIAxios({
        ...APIRoutes.getInvoicingProgression(),
        params: {
          ...requestFilters,
          date: filters.date.toISOString(),
          granularity: filters.mode,
        }
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<InvoicingProgression>) => {
        return response.data;
      }),
      tap((donutChart) => {
        this.store.update(
          (state) => ({
            ...state,
            finance: {...state.finance, donutChart},
          }),
          getInvoicingProgression.setSuccess()
        );
      }),
      getInvoicingProgression.trackRequestStatus()
    );
  };

  getMarginByLine = (filters: StatisticsFilters): Observable<MarginByLine> => {
    const requestFilters = TableFilter.toRequestFilters(filters.tableFilters);
    return from(
      APIAxios({
        ...APIRoutes.GETMarginByLine(),
        params: {
          ...requestFilters,
          date: filters.date.toISOString(),
          granularity: filters.mode,
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<MarginByLine>) => {
        return response.data;
      }),
      tap((marginByLine) => {
        this.store.update(
          (state) => ({
            ...state,
            finance: {...state.finance, marginByLine},
          }),
          getMarginByLineDataSource.setSuccess()
        );
      }),
      getMarginByLineDataSource.trackRequestStatus()
    );
  };

  getTurnoverChargesByServiceKind = (filters: StatisticsFilters): Observable<TurnoverChargesByServiceKind[]> => {
    const requestFilters = TableFilter.toRequestFilters(filters.tableFilters);
    return from(
      APIAxios({
        ...APIRoutes.GETTurnoverChargesByServiceKind(),
        params: {
          ...requestFilters,
          date: filters.date.toISOString(),
          granularity: filters.mode,
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<TurnoverChargesByServiceKind[]>) => {
        return response.data;
      }),
      tap((turnoverAndChargesByServiceKind) => {
        this.store.update(
          (state) => ({
            ...state,
            finance: {...state.finance, turnoverAndChargesByServiceKind},
          }),
          getTurnoverChargesServiceKind.setSuccess()
        );
      }),
      getTurnoverChargesServiceKind.trackRequestStatus()
    );
  };

  //OPERATIONS

  getEvolutionOfOperations = (filters: StatisticsFilters): Observable<EvolutionOfOperations> => {
    const requestFilters = TableFilter.toRequestFilters(filters.tableFilters);
    return from(
      APIAxios({
        ...APIRoutes.GETEvolutionOfOperations(),
        params: {
          ...requestFilters,
          date: filters.date.toISOString(),
          granularity: filters.mode,
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<EvolutionOfOperations>) => {
        return response.data;
      }),
      tap((evolutionOfOperations) => {
        this.store.update(
          (state) => ({
            ...state,
            operations: {...state.operations, evolutionOfOperations},
          }),
          getEvolutionOfOperationsDataSource.setSuccess()
        );
      }),
      getEvolutionOfOperationsDataSource.trackRequestStatus()
    );
  };

  getValoWeigtByWasteFamily(filters: StatisticsFilters) {
    const requestFilters = TableFilter.toRequestFilters(filters.tableFilters);
    return from(APIAxios({
        ...APIRoutes.GETValoWeightByWasteFamily(),
        params: {
          ...requestFilters,
          date: filters.date.toISOString(),
          granularity: filters.mode,
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ValoWeightByWasteFamily[]>) => {
        return response.data;
      }),
      tap((v) => {
        this.store.update(
          (state) => ({
            ...state,
            finance: {...state.finance, valoWeightByWasteFamily: v},
          }),
          getValoWeightByWasteFamily.setSuccess()
        );
      }),
      getValoWeightByWasteFamily.trackRequestStatus()
    );
  }

  getExecutionCountByServiceKind(filters: StatisticsFilters) {
    const requestFilters = TableFilter.toRequestFilters(filters.tableFilters);
    return from(APIAxios({
        ...APIRoutes.GETExecutionCountByServiceKind(),
        params: {
          ...requestFilters,
          date: filters.date.toISOString(),
          granularity: filters.mode,
        },
      })
    ).pipe(
      catchError((err: AxiosError) => {
        throw new SnackError((err.response as any)?.data?.message, 'error');
      }),
      map((response: AxiosResponse<ExecutionCountByServiceKind[]>) => {
        return response.data;
      }),
      tap((v) => {
        this.store.update(
          (state) => ({
            ...state,
            finance: {...state.finance, executionCountByServiceKind: v},
          }),
          getExecutionCountAll.setSuccess(),
          getExecutionCountPickup.setSuccess(),
          getExecutionCountProcessingValo.setSuccess(),
          getExecutionCountOther.setSuccess(),
          getExecutionCountRenting.setSuccess(),
        );
      }),
      getExecutionCountAll.trackRequestStatus(),
      getExecutionCountPickup.trackRequestStatus(),
      getExecutionCountProcessingValo.trackRequestStatus(),
      getExecutionCountOther.trackRequestStatus(),
      getExecutionCountRenting.trackRequestStatus()
    );
  }
}

export const statisticsService = new StatisticsService();
